import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { db, auth } from '../../../firebase';
import { collection, query, where, onSnapshot, doc } from 'firebase/firestore';
import * as financeService from '../../../services/financeService';
import './BudgetTracker.css';

const BudgetTracker = () => {
  const [transactions, setTransactions] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showIncomeModal, setShowIncomeModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [newCategory, setNewCategory] = useState('');
  const [categoryError, setCategoryError] = useState('');
  const [newIncome, setNewIncome] = useState('');
  const [incomeError, setIncomeError] = useState('');
  const [newTransaction, setNewTransaction] = useState({
    amount: '',
    description: ''
  });
  const [transactionError, setTransactionError] = useState('');
  const [monthlyIncome, setMonthlyIncome] = useState(5000);
  const [categories, setCategories] = useState(['Food', 'Transport', 'Housing', 'Entertainment', 'Utilities', 'Other']);
  const [loading, setLoading] = useState(true);

  // Initialize and load user's data from Firebase
  useEffect(() => {
    if (!auth.currentUser) return;

    const userId = auth.currentUser.uid;
    
    // Initialize user's finances if needed
    financeService.initializeUserFinances(userId);

    // Load monthly income
    const userDocRef = doc(db, 'users', userId);
    const unsubIncome = onSnapshot(userDocRef, (doc) => {
      if (doc.exists() && doc.data().monthlyIncome !== undefined) {
        setMonthlyIncome(doc.data().monthlyIncome);
      }
    });

    // Load categories
    const categoriesRef = collection(db, 'users', userId, 'categories');
    const unsubCategories = onSnapshot(categoriesRef, (snapshot) => {
      const loadedCategories = snapshot.docs.map(doc => doc.data().name);
      if (loadedCategories.length > 0) {
        setCategories(loadedCategories);
      }
    });

    // Load transactions
    const transactionsRef = collection(db, 'users', userId, 'transactions');
    const unsubTransactions = onSnapshot(
      query(transactionsRef, where('type', '==', 'expense')),
      (snapshot) => {
        const loadedTransactions = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          date: doc.data().date.toDate().toISOString()
        }));
        setTransactions(loadedTransactions);
        setLoading(false);
      }
    );

    return () => {
      unsubIncome();
      unsubCategories();
      unsubTransactions();
    };
  }, []);

  const handleModalClose = () => {
    setShowCategoryModal(false);
    setNewCategory('');
    setCategoryError('');
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    const trimmedCategory = newCategory.trim();
    
    if (!trimmedCategory) {
      setCategoryError('Category name is required');
      return;
    }

    if (categories.includes(trimmedCategory)) {
      setCategoryError('Category already exists');
      return;
    }

    if (trimmedCategory.length > 20) {
      setCategoryError('Category name must be less than 20 characters');
      return;
    }

    try {
      await financeService.addCategory(auth.currentUser.uid, trimmedCategory);
      handleModalClose();
    } catch (error) {
      setCategoryError('Failed to add category. Please try again.');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!newTransaction.amount || !activeCategory) return;

    setTransactions([
      ...transactions,
      {
        ...newTransaction,
        id: Date.now().toString(),
        date: new Date().toISOString(),
        category: activeCategory,
        type: 'expense',
        amount: Number(newTransaction.amount)
      }
    ]);

    setNewTransaction({ amount: '', description: '' });
    setActiveCategory(null);
  };

  const totalExpenses = transactions.reduce((sum, t) => sum + t.amount, 0);
  const remaining = monthlyIncome - totalExpenses;

  const getCategoryTotal = (category) => {
    return transactions
      .filter(t => t.category === category)
      .reduce((sum, t) => sum + t.amount, 0);
  };

  const handleDeleteTransaction = async (transactionId) => {
    if (!auth.currentUser) return;
    await financeService.deleteTransaction(auth.currentUser.uid, transactionId);
  };

  const handleDeleteClick = (category, e) => {
    e.stopPropagation();
    setCategoryToDelete(category);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!categoryToDelete || !auth.currentUser) return;
    await financeService.deleteCategory(auth.currentUser.uid, categoryToDelete);
    setShowDeleteModal(false);
    setCategoryToDelete(null);
  };

  const handleExpenseClick = (category, e) => {
    e.stopPropagation();
    setActiveCategory(category);
    setShowExpenseModal(true);
    setNewTransaction({ amount: '', description: '' });
    setTransactionError('');
  };

  const handleExpenseClose = () => {
    setShowExpenseModal(false);
    setActiveCategory(null);
    setNewTransaction({ amount: '', description: '' });
    setTransactionError('');
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    
    if (!auth.currentUser) return;
    
    if (!newTransaction.description.trim()) {
      setTransactionError('Description is required');
      return;
    }

    const amount = parseFloat(newTransaction.amount);
    if (isNaN(amount) || amount <= 0) {
      setTransactionError('Please enter a valid amount');
      return;
    }

    const transactionData = {
      description: newTransaction.description.trim(),
      amount: amount,
      category: activeCategory,
      type: 'expense',
      date: new Date()
    };

    try {
      await financeService.addTransaction(auth.currentUser.uid, transactionData);
      handleExpenseClose();
    } catch (error) {
      setTransactionError('Failed to add expense. Please try again.');
    }
  };

  const handleIncomeClick = () => {
    setNewIncome(monthlyIncome.toString());
    setIncomeError('');
    setShowIncomeModal(true);
  };

  const handleIncomeClose = () => {
    setShowIncomeModal(false);
    setNewIncome('');
    setIncomeError('');
  };

  const handleUpdateIncome = async (e) => {
    e.preventDefault();
    
    if (!auth.currentUser) return;
    
    const amount = parseFloat(newIncome);
    if (isNaN(amount) || amount <= 0) {
      setIncomeError('Please enter a valid amount');
      return;
    }

    try {
      await financeService.updateMonthlyIncome(auth.currentUser.uid, amount);
      handleIncomeClose();
    } catch (error) {
      setIncomeError('Failed to update income. Please try again.');
    }
  };

  const renderEmptyState = () => (
    <div className="empty-state">
      <h3>Welcome to Budget Tracker!</h3>
      <p>Start by setting up your monthly income and adding some expense categories.</p>
      <div className="empty-state-actions">
        <button 
          className="add-category-btn"
          onClick={() => setShowCategoryModal(true)}
        >
          Add Category
        </button>
      </div>
    </div>
  );

  return (
    <div className="budget-tracker">
      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <>
          <div className="summary">
            <div className="summary-item" onClick={handleIncomeClick} style={{ cursor: 'pointer' }}>
              <h3>Monthly Income</h3>
              <p className="positive">${monthlyIncome.toFixed(2)}</p>
            </div>
            <div className="summary-item expenses">
              <h3>Total Expenses</h3>
              <p className="negative">${totalExpenses.toFixed(2)}</p>
            </div>
            <div className="summary-item">
              <h3>Remaining</h3>
              <p className={remaining >= 0 ? 'positive' : 'negative'}>
                {remaining >= 0 ? '$' : '-$'}{Math.abs(remaining).toFixed(2)}
              </p>
            </div>
          </div>

          <div className="categories-section">
            <div className="section-header">
              <h2 className="section-title">Expense Categories</h2>
              <button 
                className="add-category-btn"
                onClick={() => setShowCategoryModal(true)}
              >
                Add Category
              </button>
            </div>
            {categories.length === 0 ? (
              renderEmptyState()
            ) : (
              <div className="categories-grid">
                {categories.map(category => (
                  <div key={category} className="category-card">
                    <div className="category-header">
                      <div className="category-info">
                        <h3>{category}</h3>
                        <span className="category-total">
                          ${getCategoryTotal(category).toFixed(2)}
                        </span>
                      </div>
                      <button 
                        className="add-expense-btn"
                        onClick={(e) => handleExpenseClick(category, e)}
                      >
                        +
                      </button>
                    </div>
                    <div className="category-content">
                      <div className="category-transactions">
                        {transactions
                          .filter(t => t.category === category)
                          .map(transaction => (
                            <div key={transaction.id} className="transaction-item">
                              <div className="transaction-info">
                                <p>{transaction.description}</p>
                                <span className="date">
                                  {new Date(transaction.date).toLocaleDateString()}
                                </span>
                              </div>
                              <div className="transaction-actions">
                                <p className="amount">
                                  ${transaction.amount.toFixed(2)}
                                </p>
                                <button 
                                  className="delete-btn"
                                  onClick={() => handleDeleteTransaction(transaction.id)}
                                >
                                  ×
                                </button>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                    <button
                      className="delete-category-btn"
                      onClick={(e) => handleDeleteClick(category, e)}
                      aria-label={`Delete ${category} category`}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <AnimatePresence>
            {showCategoryModal && (
              <motion.div 
                className="modal-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={handleModalClose}
              >
                <motion.div 
                  className="category-modal"
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.95, opacity: 0 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="modal-header">
                    <h3>Add New Category</h3>
                  </div>
                  <form onSubmit={handleAddCategory}>
                    <input
                      type="text"
                      className={`input-field ${categoryError ? 'error' : ''}`}
                      placeholder="Enter category name"
                      value={newCategory}
                      onChange={(e) => {
                        setNewCategory(e.target.value);
                        setCategoryError('');
                      }}
                      autoFocus
                      maxLength={20}
                    />
                    {categoryError && (
                      <div className="error-message">{categoryError}</div>
                    )}
                    <div className="modal-actions">
                      <button 
                        type="button" 
                        className="cancel-btn"
                        onClick={handleModalClose}
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit" 
                        className="add-btn"
                        disabled={!newCategory.trim()}
                      >
                        Add
                      </button>
                    </div>
                  </form>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {showDeleteModal && (
              <motion.div 
                className="modal-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setShowDeleteModal(false)}
              >
                <motion.div 
                  className="category-modal"
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.95, opacity: 0 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <p className="delete-message">
                    Are you sure you want to delete "{categoryToDelete}"?
                  </p>
                  <div className="modal-actions">
                    <button 
                      type="button" 
                      className="cancel-btn"
                      onClick={() => setShowDeleteModal(false)}
                    >
                      Cancel
                    </button>
                    <button 
                      type="button" 
                      className="delete-btn-confirm"
                      onClick={handleConfirmDelete}
                    >
                      Delete
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {showExpenseModal && (
              <motion.div 
                className="modal-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={handleExpenseClose}
              >
                <motion.div 
                  className="expense-modal"
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.95, opacity: 0 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <p className="expense-category">
                    Adding expense to <span>{activeCategory}</span>
                  </p>
                  <form onSubmit={handleAddExpense}>
                    <div className="expense-inputs">
                      <input
                        type="text"
                        className="expense-input"
                        placeholder="Description"
                        value={newTransaction.description}
                        onChange={(e) => {
                          setNewTransaction(prev => ({ ...prev, description: e.target.value }));
                          setTransactionError('');
                        }}
                        autoFocus
                      />
                      <input
                        type="number"
                        className="expense-input"
                        placeholder="Amount"
                        value={newTransaction.amount}
                        onChange={(e) => {
                          setNewTransaction(prev => ({ ...prev, amount: e.target.value }));
                          setTransactionError('');
                        }}
                        step="0.01"
                        min="0"
                      />
                    </div>
                    {transactionError && (
                      <div className="error-message">{transactionError}</div>
                    )}
                    <div className="modal-actions">
                      <button 
                        type="button" 
                        className="cancel-btn"
                        onClick={handleExpenseClose}
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit" 
                        className="add-btn"
                        disabled={!newTransaction.description.trim() || !newTransaction.amount}
                      >
                        Add
                      </button>
                    </div>
                  </form>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {showIncomeModal && (
              <motion.div 
                className="modal-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={handleIncomeClose}
              >
                <motion.div 
                  className="expense-modal"
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.95, opacity: 0 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <p className="expense-category">
                    Update Monthly Income
                  </p>
                  <form onSubmit={handleUpdateIncome}>
                    <div className="expense-inputs">
                      <input
                        type="number"
                        className="expense-input"
                        placeholder="Enter monthly income"
                        value={newIncome}
                        onChange={(e) => {
                          setNewIncome(e.target.value);
                          setIncomeError('');
                        }}
                        step="0.01"
                        min="0"
                        autoFocus
                      />
                    </div>
                    {incomeError && (
                      <div className="error-message">{incomeError}</div>
                    )}
                    <div className="modal-actions">
                      <button 
                        type="button" 
                        className="cancel-btn"
                        onClick={handleIncomeClose}
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit" 
                        className="add-btn"
                        disabled={!newIncome || parseFloat(newIncome) <= 0}
                      >
                        Update
                      </button>
                    </div>
                  </form>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </div>
  );
};

export default BudgetTracker;
